import type { DOMAttributes, PropsWithChildren } from 'react';
import React, { useEffect } from 'react';
import type { AnyObjectSchema } from 'yup';
import type { DefaultValues, SubmitHandler, UseFormProps } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

export type FormWrapperProps<FormValues extends Record<string, any>> = {
    formOptions?: UseFormProps<FormValues>;
    validationSchema?: AnyObjectSchema;
    onSubmit: SubmitHandler<FormValues>;
    autoResetOnDefaultValues?: boolean;
    formId?: string;
    formProps?: DOMAttributes<HTMLFormElement>;
    /**
     * Reports react-hook-form's `isDirty`, and `false` on unmount, for a parent above the form
     * (a dialog keeping a stray close from losing the input). Pass a stable callback.
     */
    onDirtyChange?: (isDirty: boolean) => void;
};

interface FormWrapperContext {
    formId: string | null;
}

export const FormContext = React.createContext<FormWrapperContext>({ formId: null });

function FormWrapper<FormValues>({
    formOptions = {},
    validationSchema,
    onSubmit,
    autoResetOnDefaultValues = true,
    formId,
    children,
    formProps,
    onDirtyChange,
}: PropsWithChildren<FormWrapperProps<FormValues>>) {
    const methods = useForm<FormValues>({
        mode: 'all',
        resolver: validationSchema ? yupResolver(validationSchema) : undefined,
        ...formOptions,
    });

    useEffect(() => {
        if (methods && autoResetOnDefaultValues === true) {
            methods.reset(formOptions?.defaultValues as DefaultValues<FormValues>);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formOptions?.defaultValues, autoResetOnDefaultValues]);

    // Read only when asked for: reading formState subscribes the form to re-render on it.
    const isDirty = onDirtyChange != null && methods.formState.isDirty;
    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

    return (
        <FormContext.Provider value={{ formId }}>
            <FormProvider {...(methods as any)}>
                <form onSubmit={methods.handleSubmit(onSubmit)} id={formId} {...formProps}>
                    {children}
                </form>
            </FormProvider>
        </FormContext.Provider>
    );
}

export default FormWrapper;
